import { useEffect, useState } from "react";
import { Zap, AlertTriangle, X } from "lucide-react";
import { api } from "../../lib/api";
import { CREDIT_TIERS } from "../../data/finance";
import StepShell from "./StepShell";

/**
 * Step 6 — Rate / Credit.
 *
 * Restores the previous credit-tier picker + Special APR selector that
 * lived inside PaymentCalculator. Loads `special_financing` from
 * /api/incentives/vehicle/{vin} (same endpoint Step 5 already calls)
 * so the customer can choose a Special APR offer when one is available.
 *
 * Special APR conflict behaviour (restored from prior implementation):
 *   • Selecting a Special APR runs /api/incentives/check against every
 *     currently-selected rebate. Any rebate that conflicts is REMOVED
 *     and surfaced to the customer via an inline notice.
 *   • Deselecting Special APR restores normal behaviour — selected
 *     rebates stay where they were, no re-add of removed rebates.
 *
 * Strict guardrails:
 *   • This step DOES NOT touch APR math or doc/state/title fees. It
 *     only stores the customer's selections — PaymentCalculator math
 *     in BuildMyOwnDeal.js / ReviewStep.js reads them.
 */
const TERMS = [36, 48, 60, 72, 84];

export default function RateStep({ byod, vin, vehicle, onContinue, onBack, firstName }) {
  const isNew = (vehicle?.status || "").toLowerCase() === "new";
  const [special, setSpecial] = useState([]);
  const [loading, setLoading] = useState(false);
  const [removedConflict, setRemovedConflict] = useState(null); // { apr_name, removed:[{id,name}] }
  const rate = byod.state.rate || {};

  useEffect(() => {
    if (!vin || !isNew) return undefined;
    let on = true;
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get(`/incentives/vehicle/${vin}`);
        if (on) setSpecial(data?.special_financing || []);
      } catch { /* ignore */ }
      finally { if (on) setLoading(false); }
    })();
    return () => { on = false; };
  }, [vin, isNew]);

  const setCreditTier = (tier) => byod.update("rate", { credit_tier: tier });
  const setTerm = (term) => byod.update("rate", { term });

  const selectSpecial = async (offer) => {
    // Check each currently-selected rebate against the candidate Special APR.
    // Anything that conflicts gets removed; surface a notice the customer
    // sees and can dismiss.
    const reb = byod.state.rebates || {};
    const selectedRebates = reb.selected || [];
    const removed = [];
    const kept = [];
    for (const r of selectedRebates) {
      try {
        const { data: chk } = await api.post("/incentives/check", {
          candidate_id: offer.incentive_id,
          selected_ids: [r.incentive_id],
          deal_type: "finance",
        });
        if (chk.ok) kept.push(r);
        else removed.push({ id: r.incentive_id, name: r.name, reason: chk.reason });
      } catch {
        // On API error, conservatively keep the rebate; the backend will
        // re-check when the lead is submitted.
        kept.push(r);
      }
    }
    if (removed.length > 0) {
      byod.replaceSection("rebates", {
        ...reb,
        selected: kept,
        total_savings_estimated: kept.reduce((s, o) => s + (Number(o.amount) || 0), 0),
      });
      setRemovedConflict({ apr_name: offer.name, removed });
    }
    byod.update("rate", {
      special_apr_id: offer.incentive_id,
      special_apr_pct: offer.apr_pct ?? offer.apr ?? null,
      special_apr_term: offer.term_months ?? offer.term ?? null,
      special_apr_name: offer.name,
    });
  };

  const clearSpecial = () => {
    byod.update("rate", {
      special_apr_id: null, special_apr_pct: null, special_apr_term: null, special_apr_name: null,
    });
    setRemovedConflict(null);
  };

  return (
    <StepShell
      title={firstName ? `${firstName}, let's pick your rate.` : "Let's pick your rate."}
      subtitle="Estimate your credit range so we can ballpark a payment. If a Special APR offer is available for this vehicle, you can take that instead — Scott will confirm everything with you."
      onBack={onBack}
      onContinue={onContinue}
      testIdPrefix="byod-rate-step"
    >
      {/* Credit tier */}
      <div className="rounded-2xl border-2 border-[var(--ssc-line)] bg-white p-4 sm:p-5" data-testid="byod-rate-credit">
        <div className="text-[10px] font-extrabold uppercase tracking-widest text-[var(--ssc-blue)]">Your credit (estimate)</div>
        <p className="text-xs text-[var(--ssc-ink-2)] mt-1">
          Pick the range that best matches your credit. We use it only to estimate a payment —
          Scott will get your real rate from the lender.
        </p>
        <div className="grid sm:grid-cols-2 gap-2 mt-3">
          {CREDIT_TIERS.map((t) => {
            const sel = rate.credit_tier === t.value;
            return (
              <button
                key={t.value}
                type="button"
                onClick={() => setCreditTier(t.value)}
                disabled={!!rate.special_apr_id}
                className={`text-center rounded-xl border-2 p-3 transition disabled:opacity-50 disabled:cursor-not-allowed ${sel ? "border-[var(--ssc-blue)] bg-[var(--ssc-blue)]/[0.06]" : "border-[var(--ssc-line)] bg-white hover:border-[var(--ssc-blue)]/40"}`}
                data-testid={`byod-rate-credit-${t.value}`}
                aria-pressed={sel}
              >
                <div className="font-extrabold text-[var(--ssc-navy)] text-sm">{t.label}</div>
              </button>
            );
          })}
        </div>

        <div className="mt-4">
          <div className="text-[10px] font-extrabold uppercase tracking-widest text-[var(--ssc-blue)]">Term</div>
          <div className="grid grid-cols-5 gap-2 mt-2">
            {TERMS.map((m) => {
              const sel = !rate.special_apr_id && rate.term === m;
              return (
                <button
                  key={m}
                  type="button"
                  onClick={() => setTerm(m)}
                  disabled={!!rate.special_apr_id}
                  className={`rounded-xl border-2 px-2 py-2 text-center text-sm font-extrabold transition disabled:opacity-50 disabled:cursor-not-allowed ${sel ? "border-[var(--ssc-blue)] bg-[var(--ssc-blue)]/[0.06] text-[var(--ssc-navy)]" : "border-[var(--ssc-line)] bg-white text-[var(--ssc-ink-2)] hover:border-[var(--ssc-blue)]/40"}`}
                  data-testid={`byod-rate-term-${m}`}
                >
                  {m}
                </button>
              );
            })}
          </div>
        </div>

        {rate.special_apr_id && (
          <p className="mt-3 text-xs text-emerald-700">
            Credit tier and term are locked while a Special APR is selected. Deselect the offer below to change them.
          </p>
        )}
      </div>

      {/* Special APR offers */}
      {isNew && (
        <div className="mt-5" data-testid="byod-rate-special-apr">
          <div className="flex items-center gap-2">
            <Zap size={16} className="text-emerald-700" />
            <h3 className="font-extrabold text-[var(--ssc-navy)]">Special APR offers</h3>
          </div>
          <p className="text-xs text-[var(--ssc-ink-2)] mt-1">
            GM occasionally offers special financing rates on this vehicle. Selecting one
            uses that rate and term for your payment estimate.
          </p>
          {loading && <p className="text-sm text-[var(--ssc-ink-2)] mt-3">Loading special offers...</p>}

          {!loading && special.length === 0 && (
            <p className="text-sm text-[var(--ssc-ink-2)] mt-3" data-testid="byod-rate-special-apr-empty">
              No Special APR offers active for this vehicle right now. Your standard estimate above stands.
            </p>
          )}

          <ul className="space-y-2 mt-3">
            {special.map((o) => {
              const sel = rate.special_apr_id === o.incentive_id;
              return (
                <li
                  key={o.incentive_id}
                  className={`rounded-2xl border-2 p-3 sm:p-4 transition ${sel ? "border-emerald-500 bg-emerald-50/50" : "border-[var(--ssc-line)] bg-white"}`}
                  data-testid={`byod-rate-special-${o.incentive_id}`}
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <div className="font-extrabold text-[var(--ssc-navy)]">{o.name}</div>
                      <div className="text-xs text-[var(--ssc-ink-2)] mt-0.5">
                        {(o.apr_pct ?? o.apr) != null ? `${Number(o.apr_pct ?? o.apr).toFixed(2)}% APR` : ""}
                        {(o.term_months ?? o.term) != null ? ` · ${o.term_months ?? o.term} months` : ""}
                      </div>
                    </div>
                    {sel ? (
                      <button type="button" onClick={clearSpecial} className="ssc-btn ssc-btn-outline !py-1.5 !text-xs" data-testid={`byod-rate-special-clear`}>
                        Deselect
                      </button>
                    ) : (
                      <button type="button" onClick={() => selectSpecial(o)} className="ssc-btn ssc-btn-primary !py-1.5 !text-xs" data-testid={`byod-rate-special-pick-${o.incentive_id}`}>
                        Use this APR
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>

          {/* Removed-rebate notice — restored prior conflict-modal copy */}
          {removedConflict && (
            <div className="mt-4 rounded-2xl border-2 border-amber-400 bg-amber-50 p-4" data-testid="byod-rate-conflict-removed">
              <div className="flex items-start gap-2.5">
                <AlertTriangle size={18} className="text-amber-700 mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-extrabold text-amber-900">
                    Some rebates can&rsquo;t be combined with {removedConflict.apr_name}
                  </div>
                  <p className="text-sm text-amber-900 mt-1">
                    We removed the following selection so this Special APR can be used. You can
                    deselect the APR above to add them back.
                  </p>
                  <ul className="mt-2 ml-5 list-disc text-sm text-amber-900">
                    {removedConflict.removed.map((r) => (
                      <li key={r.id}><strong>{r.name}</strong>{r.reason ? ` — ${r.reason}` : ""}</li>
                    ))}
                  </ul>
                </div>
                <button type="button" onClick={() => setRemovedConflict(null)} className="text-amber-800 hover:text-amber-950" aria-label="Dismiss">
                  <X size={16} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </StepShell>
  );
}
